import React, { useState } from "react";
import { MapPin, Mail, Send, Instagram, Facebook, Twitter } from "lucide-react";
import { api } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", hp_url: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", msg: "" });
    try {
      await api.post("/contact", form);
      setStatus({
        state: "success",
        msg: "Thank you! Your message has been received. We'll be in touch soon.",
      });
      setForm({ name: "", email: "", subject: "", message: "", hp_url: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again.";
      setStatus({ state: "error", msg });
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <section className="px-6 md:px-10 max-w-7xl mx-auto">
        <p className="font-overline text-xs text-[#DE9E48] mb-4">— Contact</p>
        <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-none">
          Be Part of <br />
          <span className="italic text-[#DE9E48]">the Story.</span>
        </h1>
        <p className="font-body text-stone-300 mt-6 max-w-2xl text-lg leading-relaxed">
          Have a question, a story, or interest in collaboration? We'd love to hear from you.
        </p>
      </section>

      <section className="px-6 md:px-10 py-20 max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
        {/* Info column */}
        <div className="md:col-span-5 space-y-10">
          <div>
            <p className="font-overline text-xs text-[#DE9E48] mb-4">Visit</p>
            <div className="flex items-start gap-3">
              <MapPin size={20} className="mt-1 text-[#DE9E48]" />
              <div>
                <p className="font-display text-2xl">1604 2nd Avenue North</p>
                <p className="font-body text-stone-400">Birmingham, AL 35203</p>
              </div>
            </div>
          </div>

          <div>
            <p className="font-overline text-xs text-[#DE9E48] mb-4">Connect</p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com/encorebham"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://facebook.com/encorebham"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://twitter.com/encorebham"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-twitter"
                className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>

          <div>
            <p className="font-overline text-xs text-[#DE9E48] mb-4">Tickets</p>
            <a
              href="https://encorebham.ludus.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-tickets"
              className="btn-gold"
            >
              Browse Upcoming Shows
            </a>
          </div>
        </div>

        {/* Form */}
        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="md:col-span-7 border border-white/10 bg-stone-900/40 p-8 md:p-12"
        >
          <h2 className="font-display text-3xl md:text-4xl mb-8">
            Send us <span className="italic text-[#DE9E48]">a note.</span>
          </h2>

          {/* Honeypot — humans never see this; bots will fill it */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
            <label htmlFor="contact-hp-url">Website</label>
            <input
              id="contact-hp-url"
              data-testid="contact-hp-url"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp_url}
              onChange={handle("hp_url")}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="field-label">Name</label>
              <input
                data-testid="contact-name"
                required
                value={form.name}
                onChange={handle("name")}
                className="field-input"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="field-label">Email</label>
              <input
                data-testid="contact-email"
                required
                type="email"
                value={form.email}
                onChange={handle("email")}
                className="field-input"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="field-label">Subject</label>
            <input
              data-testid="contact-subject"
              value={form.subject}
              onChange={handle("subject")}
              className="field-input"
              placeholder="What's it about?"
            />
          </div>

          <div className="mt-6">
            <label className="field-label">Message</label>
            <textarea
              data-testid="contact-message"
              required
              value={form.message}
              onChange={handle("message")}
              className="field-textarea"
              rows={5}
              placeholder="Tell us more…"
            />
          </div>

          <button
            data-testid="contact-submit"
            type="submit"
            disabled={status.state === "sending"}
            className="btn-gold mt-10 disabled:opacity-50"
          >
            {status.state === "sending" ? "Sending…" : "Send Message"}
            <Send size={14} />
          </button>

          {status.state === "success" && (
            <p
              data-testid="contact-success"
              className="mt-6 font-overline text-xs text-[#DE9E48]"
            >
              ✓ {status.msg}
            </p>
          )}
          {status.state === "error" && (
            <p
              data-testid="contact-error"
              className="mt-6 font-overline text-xs text-red-400"
            >
              ✕ {status.msg}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}
